import cx from 'clsx';
import React from 'react';
import { type Control, type FieldError, useController } from 'react-hook-form';

import Editor from '@components/common/Editor/Editor';

import useTranslation from '@hooks/useTranslation';

import style from './style.module.scss';

interface IProps {
  label?: string;
  simple?: boolean;
  height?: string | number;
  required?: boolean;
  dataQa?: string;
  type?: 'woHelperText' | string;
  placeholder?: string;
  disabled?: boolean;
  hideMedia?: boolean;
}

interface IComponentProps extends IProps {
  value: string;
  onChange?: (value: string) => void;
  error?: FieldError;
}

interface IControlledProps extends IProps {
  name: string;
  control: Control<any>;
}

const Component: React.FC<IComponentProps> = ({
  value,
  onChange,
  error,
  label = '',
  simple = false,
  height = undefined,
  required = false,
  dataQa = 'editForm',
  type = '',
  placeholder = '',
  disabled = false,
  hideMedia = false,
}) => {
  const { t } = useTranslation();
  return (
    <div className={style.editorBlock} data-qa={dataQa}>
      {label && (
        <label className={cx(style.editorLabel, { [style.requiredMark]: required })}>{t(label)}</label>
      )}
      <Editor
        value={value}
        simple={simple}
        placeholder={placeholder}
        height={height}
        onChange={onChange}
        disabled={disabled}
        hideMedia={hideMedia}
      />
      {type !== 'woHelperText' && <div className={style.helperText}>{error && error.message}</div>}
    </div>
  );
};

const Controlled: React.FC<IControlledProps> = ({
  name,
  control,
  label = '',
  simple = false,
  height = undefined,
  required = false,
  dataQa = '',
  type = '',
  placeholder = '',
  disabled = false,
  hideMedia = false,
}) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });

  return (
    <Component
      value={value}
      onChange={onChange}
      error={error}
      label={label}
      simple={simple}
      height={height}
      required={required}
      dataQa={dataQa}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      hideMedia={hideMedia}
    />
  );
};

const isControlled = (props: IControlledProps | IComponentProps): props is IControlledProps =>
  !!(props as IControlledProps).control;

const EditorForm: React.FC<IControlledProps | IComponentProps> = (props) => {
  return isControlled(props) ? <Controlled {...props} /> : <Component {...props} />;
};

export default EditorForm;
